import * as React from 'react';
import {
  List,
  Datagrid,
  TextField,
  EmailField,
  FunctionField,
  useNotify,
  Confirm,
  EditButton,
  useRefresh,
  useListContext,
} from 'react-admin';
import { IconButton } from '../icon-button/IconButton';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import { useTranslation } from 'react-i18next';
import { MemberService } from '../../services/member/memberService';
import { CardActionIcons } from '../card-actions/CardActions';
import { EmptyBox } from '../empty-box/EmptyBox';
import { PageHeader } from '../page-header/PageHeader';
import * as Sentry from '@sentry/react';
import { Box, Typography, Tooltip, useMediaQuery } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { listSurfaceSx } from '../../core/theme/listStyles';
import { cardActionButtonSx } from '../../core/theme/buttonStyles';
import { isAdminRole } from '../../utils/roles';

//TODO: move styles to a separate file if possible

const MembersTableActions = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const isMobile = useMediaQuery('(max-width:600px)');
  return (
    <PageHeader
      stretchActionsOnMobile
      title={t('resources.members')}
      subtitle={t('members.subtitle')}
      actions={
        <IconButton
          icon={<AddIcon />}
          label={t('members.addMember')}
          sx={isMobile ? cardActionButtonSx : undefined}
          onClick={() => navigate('/members/create')}
        />
      }
    />
  );
};

interface MobileMembersListProps {
  setConfirmOpen: (open: boolean) => void;
  setToDelete: (id: number | null) => void;
}

const MobileMembersList = ({ setConfirmOpen, setToDelete }: MobileMembersListProps) => {
  const { data, isLoading } = useListContext();
  const { t } = useTranslation();
  const navigate = useNavigate();

  // Z jakiej restauracji jest dany user widzi tylko ADMIN/SUPER_ADMIN.
  const showRestaurant = isAdminRole();

  if (isLoading || !data) return null;

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-3)' }}>
      {data.map((record: any) => {
        // Keep the whole name (three-part names lost their last word before)
        // and fall back to the e-mail so a card is never headless.
        const fullName = (record?.name ?? '').trim();
        const displayName = fullName || (record?.email ?? '').trim() || `#${record.id}`;
        // `roles` is an array — printing it raw glued the values together
        // ("SUPER_ADMINADMIN").
        const roles: string[] = Array.isArray(record?.roles)
          ? record.roles.filter(Boolean)
          : record?.roles
            ? [String(record.roles)]
            : [];

        return (
          <Box
            key={record.id}
            sx={{
              border: '1px solid var(--color-border)',
              borderRadius: 'var(--radius-lg)',
              p: 'var(--space-4)',
              display: 'flex',
              flexDirection: 'column',
              gap: 'var(--space-3)',
              /* Long values (private-relay e-mails) must be allowed to
                 ellipsize; without minWidth:0 their intrinsic width leaks up
                 the tree and widens the whole document. */
              minWidth: 0,
            }}
          >
            {/* Row 1: name + roles + actions. Akcje sa ikonami wprost na karcie —
                kebab otwieral bottom sheet, czyli dodatkowe tapniecie i zaslonieta
                lista po to, zeby wybrac jedna z dwoch pozycji. */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)', minWidth: 0 }}>
              <Typography noWrap sx={{ fontWeight: 600, fontSize: 'var(--text-sm)', flex: 1, minWidth: 0 }}>
                {displayName}
              </Typography>
              <Box sx={{ display: 'flex', gap: 'var(--space-1)', flexShrink: 0 }}>
                {roles.map((role) => (
                  <Typography
                    key={role}
                    sx={{
                      fontSize: 'var(--text-2xs)',
                      fontWeight: 700,
                      letterSpacing: 'var(--tracking-caps)',
                      color: 'var(--color-text-secondary)',
                      backgroundColor: 'var(--color-bg-subtle)',
                      border: '1px solid var(--color-border)',
                      borderRadius: 'var(--radius-sm)',
                      px: 'var(--space-2)',
                      py: '2px',
                    }}
                  >
                    {role}
                  </Typography>
                ))}
              </Box>
              <CardActionIcons
                actions={[
                  {
                    key: 'edit',
                    label: t('common.edit'),
                    icon: <EditIcon fontSize="small" />,
                    onClick: () => navigate('/members/' + record.id + '/edit'),
                  },
                  {
                    key: 'delete',
                    label: t('common.delete'),
                    icon: <DeleteOutlineIcon fontSize="small" />,
                    destructive: true,
                    onClick: () => {
                      setToDelete(record.id ?? null);
                      setConfirmOpen(true);
                    },
                  },
                ]}
              />
            </Box>

            {/* Row 2: email | phone | restaurant */}
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-1)', minWidth: 0 }}>
              {/* When the name is missing the e-mail is already the card
                  title — don't print it twice. */}
              {displayName !== (record.email ?? '') && (
                <Typography noWrap sx={{ fontSize: 'var(--text-xs)', color: 'var(--color-text-secondary)' }}>
                  {record.email ?? ''}
                </Typography>
              )}
              <Typography sx={{ fontSize: 'var(--text-xs)', color: 'var(--color-text-secondary)' }}>
                {record.phoneNumber ?? ''}
              </Typography>
              {showRestaurant && record.locationName && (
                <Typography noWrap sx={{ fontSize: 'var(--text-xs)', color: 'var(--color-text-secondary)' }}>
                  {t('members.columns.restaurant')}: {record.locationName}
                </Typography>
              )}
            </Box>
          </Box>
        );
      })}
    </Box>
  );
};

export const MembersTable = () => {
  const { t } = useTranslation();
  const notify = useNotify();
  const memberService = new MemberService();

  const [confirmOpen, setConfirmOpen] = React.useState(false);
  const [toDelete, setToDelete] = React.useState<number | null>(null);
  const refresh = useRefresh();
  const isMobile = useMediaQuery('(max-width:600px)');
  const showRestaurant = isAdminRole();

  return (
    <>
      <MembersTableActions />
      <List
        actions={false}
        perPage={25}
        empty={
          <EmptyBox
            title={t('members.emptyBox.title')}
            description={t('members.emptyBox.description')}
            resource="members"
          />
        }
        sx={listSurfaceSx({ isMobile })}
      >
        {isMobile ? (
          <MobileMembersList setConfirmOpen={setConfirmOpen} setToDelete={setToDelete} />
        ) : (
          <Datagrid
            bulkActionButtons={false}
            rowClick={false}
            sx={{
              '& .RaDatagrid-headerCell': {
                fontWeight: 700,
                letterSpacing: 0.2,
              },
            }}
          >
            <TextField source="id" label={t('members.columns.id')} sortable={false} />
            <TextField source="roles" label={t('members.columns.role')} sortable={false} />
            <FunctionField
              sortable={false}
              label={t('members.columns.name')}
              render={(record: any) => (record?.name ?? '').trim().split(' ')[0]}
            />
            <FunctionField
              sortable={false}
              label={t('members.columns.surname')}
              render={(record: any) => (record?.name ?? '').trim().split(' ')[1]}
            />
            <EmailField source="email" label={t('members.columns.email')} sortable={false} />
            <TextField source="phoneNumber" label={t('members.columns.phone')} sortable={false} />
            {showRestaurant && (
              <TextField
                source="locationName"
                label={t('members.columns.restaurant')}
                sortable={false}
                emptyText="—"
              />
            )}
            <FunctionField
              label=""
              sortable={false}
              render={(record) => (
                <>
                  <Tooltip title={t('common.edit')}>
                    <span>
                      <EditButton
                        icon={<EditIcon fontSize="small" />}
                        label=""
                        sx={{
                          color: 'var(--color-primary)',
                          minWidth: 'unset !important',
                          padding: 'var(--space-2)',
                          borderRadius: 'var(--radius-md)',
                          '& .MuiButton-startIcon': { margin: 0 },
                          '&:hover': { backgroundColor: 'var(--color-primary-soft)' },
                        }}
                      />
                    </span>
                  </Tooltip>
                  <Tooltip title={t('common.delete')}>
                    <span>
                      <IconButton
                        icon={<DeleteOutlineIcon />}
                        color="var(--color-error)"
                        onClick={() => {
                          setConfirmOpen(true);
                          setToDelete(record.id);
                        }}
                      />
                    </span>
                  </Tooltip>
                </>
              )}
            />
          </Datagrid>
        )}
      </List>

      <Confirm
        isOpen={confirmOpen}
        title={t('members.dialog.confirmDeletionTitle')}
        content={t('members.dialog.confirmDeletionBody')}
        confirm={t('common.delete')}
        cancel={t('common.cancel')}
        onConfirm={async () => {
          try {
            await memberService.deleteMember(toDelete);
            notify(t('common.saved'), { type: 'success' });
            setConfirmOpen(false);
            refresh();
            setToDelete(null);
          } catch (error: any) {
            notify(error.response.data.error, { type: 'error' });
            console.error(error.response.data.error);
            Sentry.captureException(error);
            setConfirmOpen(false);
            setToDelete(null);
            return;
          }
        }}
        onClose={() => {
          setConfirmOpen(false);
          setToDelete(null);
        }}
      />
    </>
  );
};
